import keys from "lodash/keys";
import findKey from "lodash/findKey";
import uniqBy from "lodash/uniqBy";
import slice from "lodash/slice";
import values from "lodash/values";
import isEmpty from "lodash/isEmpty";

import moment from "moment";
import { hashString } from "../utils/strings";

class LocalApi {
  constructor(env) {
    // public members
    this.URL = "";
    this.dataset = null;
    this.filteredDataSet = null;
    this.toggleDataSet = null;
    this.postLoadCallback = null;
    this.serverSideFilteringObject = null;
    this.sortEngine = "client";
    this.endpointParams = {};

    // private members
    this._sort = null;
    this.pagination = {
      _page: 1,
      offset: 1,
      pagesize: 10
    };
    this.basicPaginationEnabled = false;
    this.moreItems = false;
    this.lessItems = false;
    this.moreItemsTableID = null;
    this.lessItemsTableID = null;
    this.paginationButtonClicked = null;
    this.filters = {};
    this.customFilter = null;
    this.customFilterId = null;
    this.customFilterType = null;
    this._search = null;
    this._toggleFilter = null;
    this._advancedFilters = null;
    this._totalItems = 0;
    this.env = env;
    this.selectedRows = [];
    this.allRowsSelected = false
  }

  setupAPI(config) {
    if (config && config.endpoint) {
      this.URL = this._getURL(config.endpoint);
    }

    if (config && config.sortEngine) {
      this.sortEngine = config.sortEngine;
    }

    if (config && config.postLoadCallback) {
      this.postLoadCallback = config.postLoadCallback;
    }

    if (config && config.endpointParams) {
      this.endpointParams = config.endpointParams;
    }

    if (config && config.basicPaginationEnabled) {
      this.basicPaginationEnabled = config.basicPaginationEnabled;
    }
  }

  updateAPI(url, keepDataset) {
    this.URL = this._getURL(url);

    if (!keepDataset) {
      this.dataset = null;
    }
  }

  _getURL(obj) {
    let url = null;

    if (typeof obj === "string") {
      url = obj;
    } else if (obj && obj.callback) {
      url = this.env.callFunction(obj.callback);
    }

    return url;
  }

  updateRow(uuid, newRow) {
    this.dataset.forEach(item => {
      if (uuid === item.uuid) {
        const allkeys = keys(newRow);

        allkeys.forEach(key => {
          item[key] = newRow[key];
        });
      }
    });
  }

  getUniquesFromColumn(columnName) {
    const output = [];

    this.dataset.forEach(datum => {
      if (datum[columnName]) {
        if (output.indexOf(datum[columnName]) < 0) {
          output.push(datum[columnName]);
        }
      }
    });

    return output;
  }

  getPagination() {
    return this.pagination;
  }

  setPagination(pagination) {
    this.pagination = Object.assign({}, pagination);
  }

  getPaginationButtonClicked() {
    return this.paginationButtonClicked;
  }

  setPaginationButtonClicked(buttonClicked) {
    this.paginationButtonClicked = buttonClicked;
  }

  getAdvancedFilters() {
    return this._advancedFilters;
  }

  getFilters() {
    return this.filters;
  }

  getFiltersAsList() {
    const output = [];

    Object.keys(this.filters).forEach(key => {
      output.push(this.filters[key]);
    });

    return output;
  }

  setFilter(newFilter) {
    this.filters[newFilter.handle] = newFilter;
  }

  clearFilters() {
    this.filters = {};
  }

  setCustomFilterId(id) {
    this.customFilterId = id;
  }

  setCustomFilter(filterName) {
    this.customFilter = filterName;
  }

  setCustomFilterType(type) {
    this.customFilterType = type;
  }

  getCustomFilterType() {
    return this.customFilterType;
  }

  getCustomFilter() {
    return this.customFilter;
  }

  getCustomFilterId() {
    return this.customFilterId;
  }

  getCustomFilterObj() {
    if (this.customFilterType && this.customFilter) {
      return {
        type: this.customFilterType,
        name: this.customFilter
      };
    }

    return null;
  }

  setSearch(searchValue) {
    this._search = searchValue;
  }

  unsetSearch() {
    this._search = null;
  }

  getSearch() {
    return this._search;
  }

  getSort() {
    return this._sort;
  }

  setSort(sort) {
    this._sort = sort;
  }

  setTotalItems(items) {
    this._totalItems = items;
  }

  getFilteredItems() {
    return this.filteredDataSet;
  }

  getTotalItems() {
    return this._totalItems;
  }

  setSelectedRows(rows, selectAll=false) {
    this.selectedRows = rows
    this.allRowsSelected = selectAll
  }

  getSelectedRows() {
    // console.log(this.filteredDataSet)
    return this.selectedRows || [];
  }

  getDataSet(callback, isPaginationChangeOnly, tableToLoad) {
    if (this.sortEngine === "server" && !this.basicPaginationEnabled) {
      if (this.dataset) {
        // if server-side filtering, fetch new dataset with POST payload
        // serverSideFilteringObject is saved for re-use in CSV Export code
        this.serverSideFilteringObject = this._getFilteringObject(
          this.getPagination(),
          this.getSort(),
          this.getFiltersAsList(),
          this.getAdvancedFilters(),
          isPaginationChangeOnly
        );
        this.fetchData(
          this.URL + this._getEndpointParams(this.endpointParams, true),
          callback,
          this.serverSideFilteringObject
        );
      } else {
        // if dataset doesn't exist, request new data with pagination only (no filters or sort)
        // serverSideFilteringObject is saved for re-use in CSV Export code
        this.serverSideFilteringObject = this._getFilteringObject(
          this.getPagination(),
          null,
          this.getFiltersAsList(),
          null,
          true
        );
        this.fetchData(
          this.URL + this._getEndpointParams(this.endpointParams, true),
          callback,
          this.serverSideFilteringObject
        );
      }
    } else if (this.sortEngine === "server" && this.basicPaginationEnabled) {
      if (this.dataset) {
        if (isPaginationChangeOnly) {
          this.updateDataSet(this.dataset, callback, isPaginationChangeOnly);
        } else {
          this.serverSideFilteringObject = this._getFilteringObject(
            this.getPagination(),
            this.getSort(),
            this.getFiltersAsList(),
            this.getAdvancedFilters(),
            isPaginationChangeOnly,
            tableToLoad
          );
          this.fetchData(
            this.URL + this._getEndpointParams(this.endpointParams, true),
            callback,
            this.serverSideFilteringObject
          );
        }
      } else {
        this.serverSideFilteringObject = this._getFilteringObject(
          this.getPagination(),
          this.getSort(),
          this.getFiltersAsList(),
          null,
          true,
          tableToLoad
        );
        this.fetchData(
          this.URL + this._getEndpointParams(this.endpointParams, true),
          callback,
          this.serverSideFilteringObject
        );
      }
    } else if (this.dataset) {
      // if dataset is already in memory, operate filters/sort/pagination on that instead
      this.updateDataSet(this.dataset, callback);
    } else {
      this.fetchData(this.URL, callback, isPaginationChangeOnly);
    }
  }

  setDataSet(dataset) {
    this.dataset = dataset;
  }

  fetchData(url, callback, payload, isPaginationChangeOnly) {
    const type = this.sortEngine || "client";

    if (!window || !window.fetch) {
      return false;
    }

    const config = {
      client: {
        method: "GET",
        credentials: "same-origin"
      },
      server: {
        method: "POST",
        credentials: "same-origin",
        body: payload ? JSON.stringify(payload) : "",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "CSRF-Token": window.csrftoken || ""
        }
      }
    };

    const currentConfig = config[type] || config.client;

    window
      .fetch(url, currentConfig)
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          return Promise.reject(new Error(response.status));
        }
      })
      .then(j => {
        // if server-side, we reset pagination from data received
        if (this.sortEngine === "server") {
          this.setTotalItems(j.total_items);
        }

        if (this.postLoadCallback) {
          try {
            this.env.callFunction(this.postLoadCallback.callback, [j]);
          } catch (e) {}
        }

        // server-side --> data array is in key "items"
        // client-side --> data array is directly the whole response
        const data = j.items || j;

        this.lessItems = false;
        this.moreItems = false;
        this.lessItemsTableID = null;
        this.moreItemsTableID = null;

        if (j.more_items) {
          this.moreItems = j.more_items.length > 0 || false;
          this.moreItemsTableID = j.more_items.length > 0 ? j.more_items : null;
        }
        if (j.less_items) {
          this.lessItems = j.less_items.length > 0 || false;
          this.lessItemsTableID = j.less_items.length > 0 ? j.less_items : null;
        }

        this.dataset = this._addUUID(data);
        this.updateDataSet(this.dataset, callback);
      })
      .catch(err => {
        const status = parseInt(err.message);

        if (err && !isNaN(status)) {
          callback({
            error: status
          });
        } else {
          callback({
            error: 404
          });
        }
      });
  }

  updateDataSet(data, callback, isPaginationChangeOnly) {
    let renderDataSet = [];
    let outputDataSet = [];
    let totalItems = 0;

    if (this.sortEngine === "server" && !this.basicPaginationEnabled) {
      // for server-side, no filtering is applied in UI
      // rendered and filtered dataset is the whole data received and total items is received in response as well
      outputDataSet = data.slice();

      renderDataSet = outputDataSet;

      this.filteredDataSet = outputDataSet;
      totalItems = this.getPagination().total; // total was updated in the fetch success
    } else {
      // make a copy of the dataset to apply filters, sorting and pagination to
      outputDataSet = data.slice();

      // reset to page 1 of results when sorting or filtering is applied
      if (!isPaginationChangeOnly) {
        const pagination = this.getPagination();

        pagination._page = 1;
        this.setPagination(pagination);
      }

      if (
        this.getAdvancedFilters() &&
        this.getAdvancedFilters().length > 0 &&
        !this.basicPaginationEnabled
      ) {
        outputDataSet = this._applyAdvancedFilters(
          outputDataSet,
          this.getAdvancedFilters()
        );
      }

      if (!this.basicPaginationEnabled) {
        outputDataSet = this._applySortFilters(outputDataSet);
      }

      if (this.getSearch() && !this.basicPaginationEnabled) {
        outputDataSet = this._filterAllLikeString(outputDataSet, {
          values: this.getSearch()
        });
      }

      if (!isPaginationChangeOnly && this.basicPaginationEnabled) {
        if (this.getPaginationButtonClicked() === "prev") {
          const pagination = this.getPagination();

          pagination.offset = Math.ceil(
            outputDataSet.length / pagination.pagesize
          );
          this.setPagination(pagination);
        }
      }

      renderDataSet = this._extractPage(outputDataSet, this.getPagination());
      this.filteredDataSet = outputDataSet;
      totalItems = outputDataSet.length;
      this.setTotalItems(totalItems);
    }

    callback(renderDataSet, this.getTotalItems());
  }

  getSavedFilters(savedFilters, tableId, callback) {
    const params = this._getSavedFiltersApiParams("GET");

    this._fetchSavedFilters("get", params, savedFilters, tableId, callback);
  }

  addSavedFilter(savedFilters, tableId, name, callback) {
    const addedFilter = {
      name,
      table_id: tableId,
      filters: this.getFiltersAsList()
    };

    const params = this._getSavedFiltersApiParams(
      "POST",
      JSON.stringify(addedFilter)
    );

    this._fetchSavedFilters("create", params, savedFilters, tableId, callback);
  }

  removeSavedFilter(savedFilters, tableId, filterId, callback) {
    const deletedFilter = {
      table_id: tableId,
      uuid: filterId
    };

    const params = this._getSavedFiltersApiParams(
      "POST",
      JSON.stringify(deletedFilter)
    );

    this._fetchSavedFilters("delete", params, savedFilters, tableId, callback);
  }

  _fetchSavedFilters(action, params, savedFilters, tableId, callback) {
    let url = `${savedFilters.endpoint}/${action}`;

    if (action === "get") {
      url += `?table_id=${tableId}`;
    }

    window
      .fetch(url, params)
      .then(response => {
        if (!response.ok) {
          if (action === "create") {
            savedFilters.error = {
              status: response.status,
              statusText: response.statusText
            };
            callback(savedFilters);
          }
          throw new Error(
            "Saved Filters - " + response.status + " " + response.statusText
          );
        }
        return response.json();
      })
      .then(json => {
        if (!json.status) {
          if (action === "create") {
            savedFilters.error = {
              status: json.status_code,
              statusText: json.message
            };
            callback(savedFilters);
          }
          throw new Error(
            "Saved Filters - " + json.status_code + " " + json.message
          );
        }
        return json;
      })
      .then(json => {
        switch (action) {
          case "get":
            savedFilters.filters = json && json.data ? json.data : [];
            break;
          case "create":
            const addedFilter = JSON.parse(params.body);

            addedFilter.uuid = json.data.uuid;
            savedFilters.filters.push(addedFilter);
            delete savedFilters.error;
            break;
          case "delete":
            savedFilters.filters = savedFilters.filters.filter(filter => {
              return filter.uuid !== json.data.uuid;
            });
        }

        callback(savedFilters);
      })
      .catch(error => console.error(error));
  }

  _getSavedFiltersApiParams(method, payload) {
    return {
      method,
      credentials: "same-origin",
      ...(payload && { body: payload })
    };
  }

  _getEndpointParams(params, serverSide) {
    const headers = {};

    if (serverSide) {
      headers.server_side_filtering = "true";
    }

    Object.keys(params).forEach(key => {
      headers[key] = params[key];
    });

    return this._getParamsString(headers);
  }

  _getParamsString(params) {
    const prefix = this.URL.indexOf("?") >= 0 ? "&" : "?";
    const str = Object.keys(params).reduce((accumulator, currentValue) => {
      return accumulator + currentValue + "=" + params[currentValue] + "&";
    }, prefix);

    return str.slice(0, -1);
  }

  _getFilteringObject(
    pagination,
    sort,
    allfilters,
    advancedFilters,
    isPaginationChangeOnly,
    tableToLoad
  ) {
    const params = {
      filters: [],
      sort: [],
      pagination: {}
    };

    if (
      this.basicPaginationEnabled &&
      this.lessItems &&
      tableToLoad === "loadPrevTable"
    ) {
      params.upTableID = this.lessItemsTableID;
    } else if (
      this.basicPaginationEnabled &&
      this.moreItems &&
      tableToLoad === "loadNextTable"
    ) {
      params.upTableID = this.moreItemsTableID;
    }
    // if any of the sorting of filtering is changing, force pagination to be page 1
    if (!isPaginationChangeOnly) {
      pagination.offset = 1;
    }

    if (allfilters && allfilters.length) {
      allfilters.forEach(filter => {
        params.filters.push(
          this._getFiltersJson(filter.type, filter.column, filter.values)
        );
      });
    }

    if (advancedFilters && advancedFilters.length) {
      advancedFilters.forEach(filter => {
        params.filters.push(
          this._getFiltersJson(filter.type, filter.column, filter.values)
        );
      });
    }

    if (this.getSearch()) {
      params.filters.push({
        type: "text",
        column: "tableheadersearch",
        value: [this.getSearch()]
      });
    }

    if (!isEmpty(sort)) {
      params.sort.push(
        this._getSortJson(sort._type, sort._sort, sort._order.toUpperCase())
      );
    }

    if (!isEmpty(pagination)) {
      params.pagination = this._getPaginationJson(
        pagination.offset,
        pagination.pagesize
      );
    }

    return params;
  }

  /**
   * Take a date object and return a string (without timezone info) in the format: 2018/03/24
   * @param date
   * @returns {string}
   * @private
   */
  _getDateWithoutTimezone(date) {
    if (!date) return "";
    return (
      date.getFullYear() +
      "/" +
      ("0" + (date.getMonth() + 1)).slice(-2) +
      "/" +
      ("0" + date.getDate()).slice(-2)
    );
  }

  _parseFilterValues(type, values) {
    const valuesArray = [];

    if (["dateSingle", "datesingle"].indexOf(type) > -1) {
      valuesArray.push(this._getDateWithoutTimezone(values));
    } else if (["daterange", "dateRange"].indexOf(type) > -1) {
      const from = values.startDate || values.fromDate;
      const to = values.endDate || values.toDate;

      valuesArray.push(
        this._getDateWithoutTimezone(from),
        this._getDateWithoutTimezone(to)
      );
    } else if (["dateselector", "dateSelector"].indexOf(type) > -1) {
      let valueObj = {};

      if (values.dateType === "dateRange" || values.dateType === "daterange") {
        const from = values.startDate || values.fromDate;
        const to = values.endDate || values.toDate;

        valueObj = {
          selectedId: values.selectedId,
          from: this._getDateWithoutTimezone(from),
          to: this._getDateWithoutTimezone(to)
        };
      } else if (
        values.dateType === "dateSingle" ||
        values.dateType === "datesingle"
      ) {
        valueObj = {
          selectedId: values.selectedId,
          date: this._getDateWithoutTimezone(values.date)
        };
      }
      valuesArray.push(valueObj);
    } else if (["listexpanded"].indexOf(type) > -1) {
      values.forEach(value => {
        valuesArray.push(value);
      });
    } else if (Array.isArray(values)) {
      values.forEach(value => {
        valuesArray.push(value.value);
      });
    } else {
      valuesArray.push(values);
    }

    return valuesArray;
  }

  _getFiltersJson(type, col, values) {
    return {
      type: this._getServerSideFilterType(type),
      column: col,
      value: this._parseFilterValues(type, values)
    };
  }

  _getSortJson(type, col, values) {
    return {
      type,
      column: col,
      value: [values]
    };
  }

  _getPaginationJson(pageNum, itemsPerPage) {
    return {
      page_number: pageNum,
      items_per_page: itemsPerPage
    };
  }

  _getServerSideFilterType(type) {
    const filtersMap = {
      value: ["list", "listexpanded", "string", "number"],
      text: ["search"],
      date: ["dateSingle"],
      date_range: ["daterange", "dateRange"]
    };

    return (
      findKey(filtersMap, filter => {
        return filter.indexOf(type) > -1;
      }) || "value"
    );
  }

  _addUUID(dataset) {
    const newdata = dataset.map(item => {
      if (!item.uuid) {
        const strifiedDataRow = JSON.stringify(item);

        item.uuid = hashString(strifiedDataRow);
      }

      return item;
    });

    return newdata;
  }

  _applyToggleFilter(limitDataSet, toggleFilter) {
    if (!this.env.checkFunction(toggleFilter.comparator)) {
      return limitDataSet;
    }

    const outputDataSet = this.env.callFunction(toggleFilter.comparator);

    return outputDataSet;
  }

  _applyAdvancedFilters(limitDataSet, advancedFilters) {
    let outputDataSet = limitDataSet.slice();

    advancedFilters.forEach(filterItem => {
      outputDataSet = outputDataSet.filter((dataItem, i) => {
        if (filterItem.type === "string") {
          if (
            dataItem[filterItem.col] &&
            dataItem[filterItem.col]
              .toString()
              .toLowerCase()
              .trim()
              .indexOf(filterItem.value.trim().toLowerCase()) > -1
          ) {
            return dataItem;
          }
        }

        if (
          filterItem.type === "daterange" &&
          filterItem.value.fromDate &&
          filterItem.value.toDate
        ) {
          let date = new Date(dataItem[filterItem.col]);

          if (date && date.getTime) {
            date = date.getTime();

            if (
              date >= filterItem.value.fromDate.getTime() &&
              date <= filterItem.value.toDate.getTime()
            ) {
              return dataItem;
            }
          }
        }

        if (filterItem.type === "list") {
          if (dataItem[filterItem.col]) {
            if (filterItem.value.length > 0) {
              for (let i = 0; i < filterItem.value.length; i++) {
                if (
                  dataItem[filterItem.col].toString().toLowerCase() ===
                  filterItem.value[i].toLowerCase()
                ) {
                  return dataItem;
                }
              }
            } else {
              return dataItem;
            }
          }
        }

        if (filterItem.type === "listexpanded") {
          if (dataItem[filterItem.col]) {
            if (filterItem.value.length > 0) {
              for (let i = 0; i < filterItem.value.length; i++) {
                const expandedValues = dataItem[filterItem.col].split(",");

                for (let ii = 0; ii < expandedValues.length; ii++) {
                  if (
                    expandedValues[ii]
                      .toString()
                      .toLowerCase()
                      .trim() === filterItem.value[i].toLowerCase()
                  ) {
                    return dataItem;
                  }
                }
              }
            } else {
              return dataItem;
            }
          }
        }

        if (filterItem.type === "number") {
          if (
            dataItem[filterItem.col] &&
            dataItem[filterItem.col] ===
              parseInt(filterItem.value.toLowerCase())
          ) {
            return dataItem;
          }
        }
      });
    });

    return uniqBy(outputDataSet, "uuid");
  }

  /**
   * Process any selected sorting and filtering and return a render-ready dataset
   * @param limitDataSet
   * @param sort
   * @param allfilters
   * @returns {*}
   * @private
   */
  _applySortFilters(limitDataSet) {
    let outputDataSet = limitDataSet;
    let filtersApplied = false;

    const filters = this.getFiltersAsList();
    const sort = this.getSort();

    if (filters && filters.length > 0) {
      filtersApplied = true;

      filters.forEach(filterItem => {
        if (filterItem.type === "list") {
          if (Array.isArray(filterItem.values)) {
            outputDataSet = this._filterLikeArray(outputDataSet, filterItem);
          } else {
            outputDataSet = this._filterLikeString(outputDataSet, filterItem);
          }
        } else if (filterItem.type === "search") {
          outputDataSet = this._filterLikeFuzzyString(
            outputDataSet,
            filterItem
          );
        } else if (filterItem.type === "searchall") {
          outputDataSet = this._filterAllLikeString(outputDataSet, filterItem);
        } else if (filterItem.type === "daterange") {
          outputDataSet = this._filterLikeDate(outputDataSet, filterItem);
        } else if (filterItem.type === "datesingle") {
          outputDataSet = this._filterLikeSingleDate(outputDataSet, filterItem);
        } else if (filterItem.type === "dateselector") {
          outputDataSet = this._filterLikeDateSelector(
            outputDataSet,
            filterItem
          );
        }
      });
    }

    if (outputDataSet.length < 1 && !filtersApplied) {
      outputDataSet = limitDataSet;
    }

    if (sort && sort._sort) {
      if (sort._type === "lexical") {
        this._sortLexical(outputDataSet, sort);
      } else if (sort._type === "numeric") {
        this._sortNumeric(outputDataSet, sort);
      } else if (sort._type === "date") {
        this._sortDate(outputDataSet, sort);
      }
    }

    return uniqBy(outputDataSet, "uuid");
  }

  /**
   * take a slice out of the active dataset for pagination
   * @param dataset
   * @param pagesize
   * @param offset
   * @returns {Array}
   * @private
   */
  _extractPage(dataset, pagination) {
    const calcOffset = (pagination.offset - 1) * pagination.pagesize;
    const calcLimit = pagination.offset * pagination.pagesize;

    return slice(dataset, calcOffset, calcLimit);
  }

  /**
   * Basic lexical sorting of the active dataset
   * @param dataset
   * @param sort
   * @private
   */
  _sortLexical(dataset, sort) {
    dataset.sort((a, b) => {
      if (!a[sort._sort]) {
        return -1;
      }

      if (!b[sort._sort]) {
        return 1;
      }

      if (a[sort._sort].toLowerCase() < b[sort._sort].toLowerCase()) {
        return -1;
      }

      if (a[sort._sort].toLowerCase() > b[sort._sort].toLowerCase()) {
        return 1;
      }

      return 0;
    });

    if (sort._order === "desc") {
      dataset = dataset.reverse();
    }
  }

  /**
   * Basic numeric sorting of the active dataset
   * @param dataset
   * @param sort
   * @private
   */
  _sortNumeric(dataset, sort) {
    dataset.sort((a, b) => {
      const aa = parseFloat(a[sort._sort]);
      const bb = parseFloat(b[sort._sort]);

      if (!aa || !bb) {
        return 0;
      }

      if (aa < bb) {
        return -1;
      }
      if (aa > bb) {
        return 1;
      }
      return 0;
    });

    if (sort._order === "desc") {
      dataset = dataset.reverse();
    }
  }

  // Devnote; do we need to handle localization here too? This could be a problem as per the date filter below.
  // If needed - pass through a moment correction and extract at midnight on the day in question (corrects for US/CN/IN etc)
  _sortDate(dataset, sort) {
    dataset.sort((a, b) => {
      const aDate = new Date(a[sort._sort]).getTime();
      const bDate = new Date(b[sort._sort]).getTime();

      return aDate - bDate;
    });

    if (sort._order === "desc") {
      dataset = dataset.reverse();
    }
  }

  /**
   * Basic string match filtering
   * @param dataset
   * @param filterSet
   * @returns {*}
   * @private
   */
  _filterLikeString(dataset, filterSet) {
    const col = filterSet.column;

    const output = dataset.filter((item, i) => {
      if (
        item[col] &&
        item[col].toLowerCase().trim() === filterSet.values.toLowerCase().trim()
      ) {
        return item;
      }
    });

    return output;
  }

  /**
   * Basic partialcstring in Array match filtering
   * @param dataset
   * @param filterSet
   * @returns {*}
   * @private
   */
  _filterLikeArray(dataset, filterSet) {
    const col = filterSet.column;
    const output = [];

    dataset.filter((item, i) => {
      filterSet.values.map(filterItem => {
        if (
          item[col] &&
          item[col]
            .toLowerCase()
            .trim()
            // .includes(filterItem.value.toLowerCase().trim()) // <- Needs String.includes polyfill
            .indexOf(filterItem.value.toLowerCase().trim()) > -1
        ) {
          output.push(item);
        }
      });
    });
    return output;
  }

  /**
   * Basic string match filtering (fuzzy)
   * @param dataset
   * @param filterSet
   * @returns {*}
   * @private
   */
  _filterLikeFuzzyString(dataset, filterSet) {
    const col = filterSet.column;

    const output = dataset.filter((item, i) => {
      if (
        item[col] &&
        item[col]
          .toLowerCase()
          .trim()
          .indexOf(filterSet.values.toLowerCase().trim()) > -1
      ) {
        return item;
      }
    });

    return output;
  }

  /**
   * Basic string match filtering - across all columns
   * @param dataset
   * @param filterSet
   * @returns {*}
   * @private
   */
  _filterAllLikeString(dataset, filterSet) {
    const output = dataset.filter((item, i) => {
      let matchflag = false;
      const allvalues = values(item);

      allvalues.forEach(subitem => {
        if (
          subitem &&
          subitem
            .toString()
            .toLowerCase()
            .indexOf(filterSet.values.toLowerCase().trim()) > -1
        ) {
          matchflag = true;
        }
      });

      if (matchflag) {
        return item;
      }
    });

    return output;
  }

  /**
   * Basic date exact matching (UTC-like by day only)
   * @param dataset
   * @param filterSet
   * @returns {*}
   * @private
   */
  _filterLikeDate(dataset, filterSet) {
    // care... date may not be formatted correctly. To be sure, cast if necessary
    let startDate;
    let endDate;

    if (
      Object.prototype.toString.call(filterSet.values.startDate) ===
      "[object Date]"
    ) {
      startDate = filterSet.values.startDate.getTime();
    } else {
      startDate = new Date(filterSet.values.startDate).getTime();
    }

    if (
      Object.prototype.toString.call(filterSet.values.endDate) ===
      "[object Date]"
    ) {
      endDate = filterSet.values.endDate.getTime();
    } else {
      endDate = new Date(filterSet.values.endDate).getTime();
    }

    const output = dataset.filter(datum => {
      try {
        // in absence of localization, normalize date to be midnight on datestring
        const momentDate = moment(datum[filterSet.column]);
        const datenum = new Date(momentDate.format("L")).getTime();

        return datenum >= startDate && datenum <= endDate;
      } catch (e) {}
    });

    return output;
  }

  /**
   * Basic date exact matching (UTC-like by single day only)
   * @param dataset
   * @param filterSet
   * @returns {*}
   * @private
   */
  _filterLikeSingleDate(dataset, filterSet) {
    const startDate = moment(filterSet.values).format("L");

    const output = dataset.filter(datum => {
      try {
        // in absence of localization, normalize date to be midnight on datestring
        const momentDate = moment(datum[filterSet.column]).format("L");

        return momentDate === startDate;
      } catch (e) {}
    });

    return output;
  }

  _filterLikeDateSelector(dataset, filterSet) {
    if (filterSet.values.date !== null && filterSet.values.date !== undefined) {
      const startDate = moment(filterSet.values.date).format("L");
      const output = dataset.filter(datum => {
        try {
          const momentDate = moment(datum[filterSet.column]).format("L");

          return momentDate === startDate;
        } catch (e) {}
      });

      return output;
    } else {
      const output = this._filterLikeDate(dataset, filterSet);

      return output;
    }
  }

  getCurrentFilteringObject() {
    const payload = Object.assign({}, this.serverSideFilteringObject);

    delete payload.pagination;

    return payload;
  }

  getRowByUUID(uuid) {
    return this.dataset.filter(datasetRow => {
      return datasetRow.uuid === uuid;
    })[0];
  }

  getCurrentDataSet() {
    return this.filteredDataSet;
  }

  getToggledDataSet() {
    return this.toggleDataSet;
  }
}

export default LocalApi;
